import React, { FC, useState, useEffect } from "react";
import api from "../../../services/api";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import CreateInstructorModal from "./CreateInstructorModal";
import ConfirmModal from "../../../components/ConfirmModal";

interface Instructor {
  _id: string;
  name: string;
  email: string;
  username?: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface InstructorStats {
  totalInstructors: number;
  activeInstructors: number;
  inactiveInstructors: number;
}

const AdminInstructors: FC = () => {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [stats, setStats] = useState<InstructorStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [instructorToToggle, setInstructorToToggle] = useState<string | null>(null);
  const [togglingInstructor, setTogglingInstructor] = useState<string | null>(null);
  const [instructorToDelete, setInstructorToDelete] = useState<string | null>(null);
  const [deletingInstructor, setDeletingInstructor] = useState<string | null>(null);

  useEffect(() => {
    fetchInstructors();
    fetchStats();
  }, []);

  const fetchInstructors = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get("/users/instructors");
      setInstructors(data);
    } catch (err) {
      console.error("Error fetching instructors:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get("/users/instructors/stats");
      setStats(data);
    } catch (err) {
      console.error("Error fetching instructor stats:", err);
    }
  };

  const handleCreateInstructor = async (instructorData: Omit<Instructor, "_id" | "createdAt" | "updatedAt">) => {
    try {
      await api.post("/users/instructors", instructorData);
      fetchInstructors();
      fetchStats();
      setShowCreateModal(false);
    } catch (error) {
      console.error("Failed to create instructor:", error);
    }
  };

  const handleToggleStatus = async () => {
    if (!instructorToToggle) return;

    try {
      setTogglingInstructor(instructorToToggle);
      const instructor = instructors.find(i => i._id === instructorToToggle);
      if (instructor) {
        await api.patch(`/users/instructors/${instructorToToggle}/status`, {
          isActive: !instructor.isActive,
        });
        fetchInstructors();
        fetchStats();
      }
    } catch (error) {
      console.error("Failed to toggle instructor status:", error);
    } finally {
      setTogglingInstructor(null);
      setInstructorToToggle(null);
    }
  };

  const handleDeleteInstructor = async () => {
    if (!instructorToDelete) return;
    
    try {
      setDeletingInstructor(instructorToDelete);
      await api.delete(`/users/instructors/${instructorToDelete}`);
      
      fetchInstructors();
      fetchStats();
      setInstructorToDelete(null);
    } catch (error) {
      console.error("Failed to delete instructor:", error);
    } finally {
      setDeletingInstructor(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="large" color="#2563eb" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Instructor Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <i className="fas fa-plus"></i>
          Add New Instructor
        </button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <i className="fas fa-users text-blue-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Instructors</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalInstructors}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <i className="fas fa-user-check text-green-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Instructors</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeInstructors}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <i className="fas fa-user-times text-red-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Inactive Instructors</p>
                <p className="text-2xl font-bold text-gray-900">{stats.inactiveInstructors}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instructors List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Instructors List</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Instructor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {instructors.map((instructor) => (
                <tr key={instructor._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <i className="fas fa-user text-gray-600"></i>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {instructor.name || instructor.username || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {instructor.username && instructor.username !== instructor.name ? instructor.username : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{instructor.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        instructor.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {instructor.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(instructor.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => setInstructorToToggle(instructor._id)}
                      disabled={togglingInstructor === instructor._id}
                      className={`mr-3 ${
                        instructor.isActive
                          ? "text-red-600 hover:text-red-900"
                          : "text-green-600 hover:text-green-900"
                      } ${togglingInstructor === instructor._id ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {togglingInstructor === instructor._id ? (
                        <LoadingSpinner size="small" />
                      ) : instructor.isActive ? (
                        "Deactivate"
                      ) : (
                        "Activate"
                      )}
                    </button>
                    <button
                      onClick={() => setInstructorToDelete(instructor._id)}
                      disabled={deletingInstructor === instructor._id}
                      className={`text-red-600 hover:text-red-900 ${
                        deletingInstructor === instructor._id ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {deletingInstructor === instructor._id ? (
                        <LoadingSpinner size="small" />
                      ) : (
                        "Delete"
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <CreateInstructorModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateInstructor}
      />

      <ConfirmModal
        isOpen={!!instructorToToggle}
        title="Toggle Instructor Status"
        message={`Are you sure you want to ${
          instructors.find(i => i._id === instructorToToggle)?.isActive ? "deactivate" : "activate"
        } this instructor?`}
        onConfirm={handleToggleStatus}
        onCancel={() => setInstructorToToggle(null)}
      />

      <ConfirmModal
        isOpen={!!instructorToDelete}
        title="Delete Instructor"
        message={`Are you sure you want to delete this instructor? This action cannot be undone.`}
        onConfirm={handleDeleteInstructor}
        onCancel={() => setInstructorToDelete(null)}
      />
    </div>
  );
};

export default AdminInstructors;
